import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import ProfileDialog from "@/components/ProfileDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowLeft, LogOut, Settings, User } from "lucide-react";
import { gravatarUrl } from "@/lib/gravatar";

export default function Navbar({ title, right, back }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = user && user.name ? user.name.slice(0, 2).toUpperCase() : "U";
  const avatarUrl = user?.email ? gravatarUrl(user.email) : null;

  return (
    <header
      className="h-14 sm:h-16 border-b border-zinc-200 dark:border-zinc-800 px-3 sm:px-6 flex items-center justify-between shrink-0 bg-white dark:bg-zinc-950 sticky top-0 z-30"
      data-testid="app-navbar"
    >
      <div className="flex items-center gap-2 sm:gap-6 min-w-0 flex-1">
        {back && (
          <button
            onClick={() => navigate(back)}
            className="h-9 w-9 -ml-1 flex items-center justify-center hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors shrink-0"
            data-testid="navbar-back"
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <Link
          to="/boards"
          className="font-display text-lg sm:text-xl font-black tracking-tighter shrink-0"
          data-testid="navbar-logo"
        >
          KANBN<span className="text-red-500">.</span>
        </Link>
        {title && (
          <>
            <span className="hidden sm:block h-5 w-px bg-zinc-200 dark:bg-zinc-800 shrink-0" />
            <h1
              className="font-display text-base sm:text-lg font-bold tracking-tight truncate text-zinc-950 dark:text-zinc-100"
              data-testid="navbar-title"
            >
              {title}
            </h1>
          </>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <ThemeToggle />
        {right}
        {user && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="h-9 w-9 border border-zinc-200 dark:border-zinc-700 hover:border-black dark:hover:border-white transition-colors overflow-hidden flex items-center justify-center"
                  data-testid="navbar-user-menu"
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={user.name}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.parentElement.textContent = initials;
                      }}
                    />
                  ) : (
                    <span className="text-xs font-bold tracking-wider">{initials}</span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none border-zinc-200 dark:border-zinc-800 w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="font-medium">{user.name}</div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setProfileOpen(true)} data-testid="navbar-profile">
                  <Settings className="h-4 w-4 mr-2" /> Profile &amp; Password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/boards")} data-testid="navbar-my-boards">
                  <User className="h-4 w-4 mr-2" /> My boards
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} data-testid="navbar-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ProfileDialog
              open={profileOpen}
              onOpenChange={setProfileOpen}
              user={user}
            />
          </>
        )}
      </div>
    </header>
  );
}
